import React, { useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  Tabs,
  Tab,
} from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ListAltIcon from '@mui/icons-material/ListAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import VerticalSplitIcon from '@mui/icons-material/VerticalSplit';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PhoneIcon from '@mui/icons-material/Phone';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PipelineBoard from '~/components/transition/pipeline/PipelineBoard';
import PipelineGroupedList from '~/components/transition/pipeline/PipelineGroupedList';
import PipelineMasterDetail from '~/components/transition/pipeline/PipelineMasterDetail';
import ReferralListTable from '~/components/transition/list/ReferralListTable';
import PhoneIntakeDialog from '~/components/transition/intake/PhoneIntakeDialog';
import FaxUploadDialog from '~/components/transition/intake/FaxUploadDialog';
import DeclinedReferralsTable from '~/components/transition/declined/DeclinedReferralsTable';
import { useTransitionContext } from '~/hooks/transition/useTransitionContext';
import { ReferralSource, ReferralStage, ReferralStatus } from '~/types/transition';

type ViewMode = 'board' | 'grouped' | 'split' | 'list';
type TopTab = 'active' | 'declined';

const TransitionPipelineView: React.FC = () => {
  const { referrals } = useTransitionContext();
  const [topTab, setTopTab] = useState<TopTab>('active');
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');

  // Filter state — shared across both pipeline and list views
  const [filterSource, setFilterSource] = useState<ReferralSource | 'ALL'>('ALL');
  const [filterStage, setFilterStage] = useState<ReferralStage | 'ALL'>('ALL');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [phoneIntakeOpen, setPhoneIntakeOpen] = useState(false);
  const [faxIntakeOpen, setFaxIntakeOpen] = useState(false);
  const [newReferralAnchor, setNewReferralAnchor] = useState<null | HTMLElement>(null);

  const activeFilterCount = [filterSource !== 'ALL', filterStage !== 'ALL'].filter(Boolean).length;

  const activeReferrals = referrals.filter(
    (r) => r.status !== ReferralStatus.DECLINED && r.status !== ReferralStatus.AUTO_DECLINED
  );
  const declinedReferrals = referrals.filter(
    (r) => r.status === ReferralStatus.DECLINED || r.status === ReferralStatus.AUTO_DECLINED
  );
  const filteredCount = activeReferrals.filter((r) => {
    if (filterSource !== 'ALL' && r.source !== filterSource) return false;
    if (filterStage !== 'ALL' && r.stage !== filterStage) return false;
    return true;
  }).length;

  const countLabel = topTab === 'declined'
    ? `${declinedReferrals.length} declined`
    : activeFilterCount > 0
    ? `${filteredCount} of ${activeReferrals.length} referrals`
    : `${activeReferrals.length} referral${activeReferrals.length !== 1 ? 's' : ''}`;

  const handleResetFilters = () => {
    setFilterSource('ALL');
    setFilterStage('ALL');
  };

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="baseline" spacing={1.5}>
          <Typography variant="h5" fontWeight={600}>
            Referral Pipeline
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {countLabel}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            endIcon={<ArrowDropDownIcon />}
            onClick={(e) => setNewReferralAnchor(e.currentTarget)}
            sx={{ height: 32 }}
          >
            New Referral
          </Button>
          <Menu
            anchorEl={newReferralAnchor}
            open={Boolean(newReferralAnchor)}
            onClose={() => setNewReferralAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MenuItem onClick={() => { setNewReferralAnchor(null); setPhoneIntakeOpen(true); }}>
              <ListItemIcon><PhoneIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Phone Referral" secondary="Enter details while on the call" />
            </MenuItem>
            <MenuItem onClick={() => { setNewReferralAnchor(null); setFaxIntakeOpen(true); }}>
              <ListItemIcon><UploadFileIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Fax / Document Upload" secondary="Upload PDF or image for AI extraction" />
            </MenuItem>
          </Menu>

          <Badge badgeContent={activeFilterCount} color="primary">
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(true)}
              sx={{ height: 32 }}
            >
              Filters
            </Button>
          </Badge>

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            size="small"
          >
            <Tooltip title="List view">
              <ToggleButton value="grouped" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <ViewListIcon sx={{ fontSize: '1rem' }} /> List
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Split view">
              <ToggleButton value="split" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <VerticalSplitIcon sx={{ fontSize: '1rem' }} /> Split
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Table view">
              <ToggleButton value="list" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <ListAltIcon sx={{ fontSize: '1rem' }} /> Table
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Board view">
              <ToggleButton value="board" sx={{ px: 1.5, gap: 0.75, fontSize: '0.78rem', fontWeight: 600 }}>
                <ViewColumnIcon sx={{ fontSize: '1rem' }} /> Board
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>

        </Stack>
      </Stack>

      {/* Filters dialog */}
      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ pt: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Source</InputLabel>
              <Select
                value={filterSource}
                label="Source"
                onChange={(e) => setFilterSource(e.target.value as ReferralSource | 'ALL')}
              >
                <MenuItem value="ALL">All sources</MenuItem>
                <MenuItem value={ReferralSource.EMAIL}>Email</MenuItem>
                <MenuItem value={ReferralSource.EFAX}>E-Fax</MenuItem>
                <MenuItem value={ReferralSource.FAX}>Fax</MenuItem>
                <MenuItem value={ReferralSource.PHONE}>Phone</MenuItem>
                <MenuItem value={ReferralSource.API}>API</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Stage</InputLabel>
              <Select
                value={filterStage}
                label="Stage"
                onChange={(e) => setFilterStage(e.target.value as ReferralStage | 'ALL')}
              >
                <MenuItem value="ALL">All stages</MenuItem>
                <MenuItem value={ReferralStage.PROCESSING}>Processing</MenuItem>
                <MenuItem value={ReferralStage.DECISION}>Decision</MenuItem>
                <MenuItem value={ReferralStage.EXECUTION}>Execution</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          {activeFilterCount > 0 && (
            <>
              <Divider sx={{ mt: 3, mb: 1 }} />
              <Button size="small" variant="text" color="error" onClick={handleResetFilters}>
                Clear all filters
              </Button>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFiltersOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Top tabs: Active / Declined */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={topTab} onChange={(_, v) => setTopTab(v)} sx={{ minHeight: 40 }}>
          <Tab
            label="Active"
            value="active"
            sx={{ minHeight: 40, fontWeight: 600 }}
          />
          <Tab
            label="Declined"
            value="declined"
            sx={{ minHeight: 40, fontWeight: 600 }}
          />
        </Tabs>
      </Box>

      {/* View */}
      {topTab === 'declined' ? (
        <DeclinedReferralsTable />
      ) : viewMode === 'board' ? (
        <PipelineBoard filterSource={filterSource} filterStage={filterStage} />
      ) : viewMode === 'grouped' ? (
        <PipelineGroupedList filterSource={filterSource} filterStage={filterStage} />
      ) : viewMode === 'split' ? (
        <PipelineMasterDetail filterSource={filterSource} filterStage={filterStage} />
      ) : (
        <ReferralListTable filterSource={filterSource} filterStage={filterStage} />
      )}

      <PhoneIntakeDialog open={phoneIntakeOpen} onClose={() => setPhoneIntakeOpen(false)} />
      <FaxUploadDialog open={faxIntakeOpen} onClose={() => setFaxIntakeOpen(false)} />
    </Box>
  );
};

export default TransitionPipelineView;
